import { useOrgStore } from '@/stores/org-store';
import { apiRequest } from '@/utils/api-request';
import { getFreshToken } from '@/utils/auth-token-provider';
import * as Sentry from '@sentry/react';

// Files above this use presigned-PUT (browser → S3 direct) to bypass
// proxy body-size limits that cause 413 errors.
const PRESIGNED_PUT_THRESHOLD = 50 * 1024 * 1024;

// 503 from the streaming endpoint means the backend's per-pod upload gate is
// at capacity. That is the only retryable failure: re-sending a multi-GB body
// after an encryption or storage error would not help. The server suggests a
// backoff via Retry-After; when the header is not readable (it must be
// CORS-exposed by the backend) a matching default applies.
const STREAMING_BUSY_STATUS = 503;
const STREAMING_BUSY_MAX_RETRIES = 3;
const STREAMING_BUSY_DEFAULT_DELAY_SECONDS = 10;
const STREAMING_BUSY_MAX_DELAY_SECONDS = 30;

// Resolves after `ms`, or rejects immediately with AbortError if the signal
// fires — so a cancel during the 503 backoff ends the retry loop at once
// instead of waiting out the delay.
const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Upload aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Upload aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort);
  });

export interface UploadProgress {
  loaded: number;
  total: number;
}

export interface UploadFileOptions {
  signal?: AbortSignal;
  onProgress?: (progress: UploadProgress) => void;
}

interface XhrUploadResult {
  status: number;
  responseText: string;
  getResponseHeader: (name: string) => string | null;
}

// XMLHttpRequest is used instead of fetch on the byte-transfer paths because
// fetch exposes no upload-progress events. The determinate progress bar in the
// canvas upload UI needs real loaded/total bytes, and xhr.upload.onprogress is
// the only browser API that provides them.
const xhrUpload = ({
  url,
  method,
  headers,
  body,
  signal,
  onProgress,
}: {
  url: string;
  method: string;
  headers: Record<string, string>;
  body: XMLHttpRequestBodyInit;
  signal?: AbortSignal;
  onProgress?: (progress: UploadProgress) => void;
}): Promise<XhrUploadResult> =>
  new Promise<XhrUploadResult>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Upload aborted', 'AbortError'));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    Object.entries(headers).forEach(([key, value]) =>
      xhr.setRequestHeader(key, value),
    );

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress({ loaded: event.loaded, total: event.total });
        }
      };
    }

    const onAbort = () => xhr.abort();
    signal?.addEventListener('abort', onAbort);
    const cleanup = () => signal?.removeEventListener('abort', onAbort);

    xhr.onload = () => {
      cleanup();
      resolve({
        status: xhr.status,
        responseText: xhr.responseText,
        getResponseHeader: (name) => xhr.getResponseHeader(name),
      });
    };
    xhr.onerror = () => {
      cleanup();
      reject(new TypeError('Failed to fetch'));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new DOMException('Upload aborted', 'AbortError'));
    };

    xhr.send(body);
  });

const buildAuthHeaders = async (): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {};
  const { authType } = useOrgStore.getState();
  const token = await getFreshToken();
  if (token) {
    if (authType === 'auth0') {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      headers['X-User-Token'] = token;
    }
  }
  return headers;
};

const parseRetryAfterSeconds = (header: string | null): number => {
  const parsed = Number(header);
  const seconds =
    Number.isFinite(parsed) && parsed > 0
      ? parsed
      : STREAMING_BUSY_DEFAULT_DELAY_SECONDS;
  return Math.min(seconds, STREAMING_BUSY_MAX_DELAY_SECONDS);
};

interface UploadPolicy {
  app_encryption_enabled: boolean;
  streaming_upload_enabled: boolean;
  streaming_upload_url: string | null;
  max_upload_size_bytes: number;
}

export interface FileRecord {
  file_id: string;
  orgid: string;
  workflow_id?: string;
  node_id?: string;
  filename: string;
  original_filename: string;
  content_type: string;
  file_size: number;
  s3_bucket: string;
  s3_key: string;
  s3_region: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  created_by_email?: string;
  metadata?: Record<string, unknown>;
}

export interface FileUploadResponse {
  success: boolean;
  message: string;
  file?: FileRecord;
  download_url?: string;
  job_id?: string;
  // Hoisted copy of file.s3_key written by backend-built envelopes (A2A file
  // references, trigger-materialized files); the upload endpoints omit it.
  s3_key?: string;
}

interface PresignedUploadResponse {
  success: boolean;
  message: string;
  upload_url?: string;
  upload_method?: string;
  upload_headers?: Record<string, string>;
  expires_in?: number;
  file?: FileRecord;
  download_url?: string;
}

const PRESIGNED_UPLOAD_FALLBACK = Symbol('presigned-upload-fallback');
type PresignedUploadFallback = typeof PRESIGNED_UPLOAD_FALLBACK;

const getFileTags = (file: File): string[] => {
  const tags: string[] = [];
  const fileType = file.type.split('/')[0];
  const fileExtension = file.name.split('.').pop()?.toLowerCase();

  if (fileType) {
    tags.push(fileType);
  }

  if (fileExtension) {
    tags.push(fileExtension);
  }

  if (file.type.includes('pdf')) tags.push('pdf');
  if (file.type.includes('image')) tags.push('image');
  if (file.type.includes('text')) tags.push('text');
  if (file.type.includes('csv')) tags.push('csv');
  if (file.type.includes('json')) tags.push('json');
  if (file.type.includes('excel') || file.type.includes('spreadsheet'))
    tags.push('spreadsheet');

  return tags.length > 0 ? tags : ['file'];
};

const buildMetadata = (file: File): Record<string, unknown> => ({
  description: `Uploaded file: ${file.name}`,
  tags: getFileTags(file),
});

// Small files (≤ the presigned threshold) keep the simple single-shot POST and
// no granular progress — they finish quickly, so the background progress UI is
// reserved for large files. `isLargeUpload` is the cutoff the callers use to
// decide whether to show that UI; keep it aligned with the routing below.
export const isLargeUpload = (file: File): boolean =>
  file.size > PRESIGNED_PUT_THRESHOLD;

const uploadFileSingleShot = async (
  file: File,
  orgId: string,
  workflowId: string,
  nodeId: string,
  signal?: AbortSignal,
): Promise<FileUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('workflow_id', workflowId);
  formData.append('node_id', nodeId);
  formData.append('metadata', JSON.stringify(buildMetadata(file)));

  const response = await apiRequest({
    url: `/files/${orgId}/upload`,
    method: 'POST',
    body: formData,
    target: 'workflow',
    formData: true,
    signal,
  });

  if (response?.detail) {
    throw new Error(response.detail);
  }
  return response.data as FileUploadResponse;
};

const fetchUploadPolicy = async (
  orgId: string,
): Promise<UploadPolicy | null> => {
  try {
    const response = await apiRequest<UploadPolicy>({
      url: `/files/${orgId}/upload-policy`,
      method: 'GET',
      target: 'workflow',
    });
    if (response?.detail || !response?.data) {
      return null;
    }
    return response.data;
  } catch (error) {
    // A failed policy lookup must never block uploads — callers fall back
    // to the existing presigned/single-shot behavior.
    Sentry.captureException(error, {
      extra: { context: 'fetch-upload-policy', orgId },
    });
    return null;
  }
};

// FastAPI rejections arrive as `{"detail": "..."}`; surface that sentence
// instead of the raw JSON so a 400 for a disallowed type reads as guidance.
export const describeUploadFailure = (
  prefix: string,
  status: number,
  responseText: string,
): string => {
  const body = responseText?.trim();
  if (!body) return `${prefix}: ${status}`;
  try {
    const parsed = JSON.parse(body) as { detail?: unknown };
    if (typeof parsed.detail === 'string' && parsed.detail) {
      return parsed.detail;
    }
  } catch {
    // Not JSON — fall through to the raw body.
  }
  return `${prefix}: ${status} — ${body.slice(0, 300)}`;
};

const uploadFileStreaming = async (
  file: File,
  workflowId: string,
  nodeId: string,
  streamingUploadUrl: string,
  options?: UploadFileOptions,
): Promise<FileUploadResponse> => {
  // Merge onto any query params already present in the policy-provided URL
  // instead of blindly appending a second '?'.
  const uploadUrl = new URL(streamingUploadUrl);
  uploadUrl.searchParams.set('filename', file.name);
  uploadUrl.searchParams.set('workflow_id', workflowId);
  uploadUrl.searchParams.set('node_id', nodeId);
  uploadUrl.searchParams.set('metadata', JSON.stringify(buildMetadata(file)));

  const headers: Record<string, string> = {
    'Content-Type': file.type || 'application/octet-stream',
    ...(await buildAuthHeaders()),
  };

  // Raw XHR — the streaming endpoint lives on a non-proxied host so the request
  // body skips the Vercel/Cloudflare proxy chain and its body-size limits, and
  // XHR (not fetch) is what reports upload progress for these large transfers.
  for (let attempt = 0; ; attempt++) {
    // Re-sending on retry is safe: the body is the File itself (a
    // re-readable Blob), not a one-shot stream.
    const response = await xhrUpload({
      url: uploadUrl.toString(),
      method: 'POST',
      headers,
      body: file,
      signal: options?.signal,
      onProgress: options?.onProgress,
    });

    if (
      response.status === STREAMING_BUSY_STATUS &&
      attempt < STREAMING_BUSY_MAX_RETRIES
    ) {
      const delaySeconds = parseRetryAfterSeconds(
        response.getResponseHeader('Retry-After'),
      );
      Sentry.captureMessage('Streaming upload at capacity, retrying', {
        level: 'info',
        extra: {
          attempt: attempt + 1,
          delaySeconds,
          fileName: file.name,
          fileSize: file.size,
        },
      });
      await sleep(delaySeconds * 1000, options?.signal);
      continue;
    }

    if (response.status < 200 || response.status >= 300) {
      throw new Error(
        describeUploadFailure(
          'Streaming upload failed',
          response.status,
          response.responseText,
        ),
      );
    }

    const data = JSON.parse(response.responseText) as FileUploadResponse;
    if (!data.success) {
      throw new Error(data.message || 'Streaming upload failed');
    }
    return data;
  }
};

const uploadFilePresigned = async (
  file: File,
  orgId: string,
  workflowId: string,
  nodeId: string,
  options?: UploadFileOptions,
): Promise<FileUploadResponse | PresignedUploadFallback> => {
  const initRes = await apiRequest<PresignedUploadResponse>({
    url: `/files/${orgId}/upload-url`,
    method: 'POST',
    target: 'workflow',
    suppressSentryStatuses: [501],
    signal: options?.signal,
    body: {
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      file_size: file.size,
      workflow_id: workflowId,
      node_id: nodeId,
      metadata: buildMetadata(file),
    },
  });

  // 501 = workspace has app-level encryption; presigned uploads can't work.
  if (initRes.statusCode === 501) {
    return PRESIGNED_UPLOAD_FALLBACK;
  }

  if (initRes?.detail || !initRes?.data?.upload_url || !initRes?.data?.file) {
    throw new Error(initRes?.detail || 'Failed to obtain presigned upload URL');
  }

  const {
    upload_url,
    upload_method = 'PUT',
    upload_headers = {},
    file: fileRecord,
    download_url,
  } = initRes.data;

  // Raw XHR — apiRequest would prepend /proxy/workflow and attach our auth
  // header, both of which invalidate the presigned-URL signature. XHR (not
  // fetch) is required so the direct-to-S3 byte transfer reports progress.
  const putRes = await xhrUpload({
    url: upload_url,
    method: upload_method,
    headers: upload_headers,
    body: file,
    signal: options?.signal,
    onProgress: options?.onProgress,
  });

  if (putRes.status < 200 || putRes.status >= 300) {
    throw new Error(
      describeUploadFailure(
        'Direct S3 upload failed',
        putRes.status,
        putRes.responseText,
      ),
    );
  }

  return {
    success: true,
    message: 'File uploaded successfully',
    file: fileRecord,
    download_url,
  };
};

export const uploadFile = async (
  file: File,
  workflowId: string,
  nodeId: string,
  options?: UploadFileOptions,
): Promise<FileUploadResponse> => {
  const orgId = useOrgStore.getState().currentOrg?.orgid;

  if (!orgId) {
    throw new Error('Organization ID not found');
  }

  if (file.size <= PRESIGNED_PUT_THRESHOLD) {
    return uploadFileSingleShot(
      file,
      orgId,
      workflowId,
      nodeId,
      options?.signal,
    );
  }

  // Encrypted workspaces cannot use presigned PUT (the browser holds no
  // encryption keys), so large files stream through the backend's dedicated
  // upload host instead. Non-encrypted workspaces keep the presigned path
  // below — browser→S3 direct stays the most efficient route for them.
  const policy = await fetchUploadPolicy(orgId);
  if (
    policy?.app_encryption_enabled &&
    policy.streaming_upload_enabled &&
    policy.streaming_upload_url
  ) {
    if (file.size > policy.max_upload_size_bytes) {
      // floor never overstates the real limit (ceil could make the message
      // claim a threshold the file doesn't actually exceed); the clamp keeps
      // a degenerate sub-1MB ceiling from displaying as "0 MB".
      const maxMb = Math.max(
        1,
        Math.floor(policy.max_upload_size_bytes / 1024 / 1024),
      );
      throw new Error(
        `File is too large to upload: ${file.name} exceeds the ${maxMb} MB limit.`,
      );
    }
    try {
      return await uploadFileStreaming(
        file,
        workflowId,
        nodeId,
        policy.streaming_upload_url,
        options,
      );
    } catch (err) {
      Sentry.captureException(err, {
        extra: {
          context: 'upload-file-streaming',
          fileName: file.name,
          fileSize: file.size,
          workflowId,
          nodeId,
        },
      });
      throw err;
    }
  }

  let result: FileUploadResponse | PresignedUploadFallback;
  try {
    result = await uploadFilePresigned(
      file,
      orgId,
      workflowId,
      nodeId,
      options,
    );
  } catch (err) {
    Sentry.captureException(err, {
      extra: {
        context: 'upload-file-presigned',
        fileName: file.name,
        fileSize: file.size,
        workflowId,
        nodeId,
      },
    });
    throw err;
  }

  if (result === PRESIGNED_UPLOAD_FALLBACK) {
    Sentry.captureMessage('Presigned upload not supported, falling back', {
      level: 'info',
      extra: { fileSize: file.size, fileName: file.name },
    });
    return uploadFileSingleShot(
      file,
      orgId,
      workflowId,
      nodeId,
      options?.signal,
    );
  }

  return result;
};
